<template>
    <div>
        <modal name="modal-edit-add-tech" height="auto" :scrollable="true" :adaptive="true">
            <div v-if="selectedLine" class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">
                        <template v-if="selectedLine.id">
                            Modifier un utilisateur
                        </template>
                        <template v-else>
                            Ajouter un utilisateur
                        </template>
                    </h2>
                    <button @click="$modal.hide('modal-edit-add-tech');">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>
                <tech-form v-model="technicienForm" @deleted="$modal.hide('modal-edit-add-tech'); fetchTechniciens()" />
                <div class="flex justify-center mt-6">
                    <button :disabled="loadingSaveTech" @click="editAddTechnicien" class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2">
                        <template v-if="loadingSaveTech">
                            <i class="fas fa-spinner fa-spin"></i> Enregistrement...
                        </template>
                        <template v-if="!loadingSaveTech">
                            <i class="fas fa-check"></i> Enregistrer l'utilisateur
                        </template>
                    </button>
                </div>
            </div>
        </modal>

        <app-template>
            <section class="space-y-8">

                <!-- Header -->
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <i class="fas fa-users text-red-600"></i> Utilisateurs
                        </h2>
                        <p class="text-sm text-gray-500 mt-1">Gérez les comptes de vos utilisateurs</p>
                    </div>
                    <button v-if="!$account.account.societe.solo" @click="technicienForm = {}; selectedLine = {}; $modal.show('modal-edit-add-tech')" class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2">
                        <i class="fas fa-plus"></i> Ajouter un utilisateur
                    </button>
                </div>

                <!-- Filtres -->
                <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <label class="text-sm font-medium text-gray-700">Rechercher</label>
                    <div class="relative mt-1">
                        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                        <input v-model="filters.search" type="text" placeholder="Nom de l'utilisateur, ..." class="pl-9 w-full rounded-lg border border-gray-300 focus:outline-none text-sm py-2">
                    </div>
                </div>

                <!-- Tableau -->
                <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative">
                    <table class="w-full text-sm text-left">
                        <thead class="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
                            <tr>
                                <th class="px-6 py-3">Nom</th>
                                <th class="px-6 py-3">Prénom</th>
                                <th class="px-6 py-3">Email</th>
                                <th class="px-6 py-3">Technicien</th>
                            </tr>
                        </thead>

                        <!-- Skeleton loader -->
                        <tbody v-if="loading" class="divide-y divide-gray-100">
                            <tr v-for="n in 1" :key="n" class="animate">
                                <td v-for="i in 4" :key="i" class="px-6 py-3">
                                    <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                                </td>
                            </tr>
                        </tbody>

                        <!-- Données réelles -->
                        <tbody v-else class="divide-y divide-gray-100">
                            <tr v-for="technicien in techniciens.data" :key="technicien.id"
                                @click="selectedLine = technicien; technicienForm = { ...technicien }; $modal.show('modal-edit-add-tech')"
                                class="hover:bg-red-100 cursor-pointer transition">
                                <td class="px-6 py-3 font-bold">{{ technicien.nom }}</td>
                                <td class="px-6 py-3">{{ technicien.prenom || '-' }}</td>
                                <td class="px-6 py-3">{{ technicien.email }}</td>
                                <td class="px-6 py-3">
                                    <span :class="[
                                        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                                        technicien.can_app_mobile ? 'bg-red-50 text-red-800' : 'bg-gray-50 text-gray-500'
                                      ]">
                                        <i class="fas fa-wrench" :class="technicien.can_app_mobile ? 'text-red-500' : 'text-gray-400'"></i>
                                        {{ technicien.can_app_mobile ? 'Accès mobile' : 'Sans accès mobile' }}
                                    </span>
                                </td>
                            </tr>
                            <tr v-if="!techniciens.data.length">
                                <td colspan="6" class="px-6 py-4 text-center text-gray-400 italic">
                                    Aucun technicien trouvé
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div class="flex justify-between items-center text-sm text-gray-600 mt-4">
                    <p>
                        Affichage de {{ techniciens.from || 0 }} à {{ techniciens.to || 0 }}
                        sur {{ techniciens.total || 0 }} techniciens
                    </p>

                    <div class="flex gap-1">
                        <button :disabled="!techniciens.prev_page_url" @click="fetchTechniciens(techniciens.current_page - 1)"
                            class="px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 flex items-center gap-1 disabled:opacity-50">
                            <i class="fas fa-chevron-left text-xs"></i> Précédent
                        </button>

                        <span v-for="page in pages" :key="page" @click="fetchTechniciens(page)"
                            :class="[
                                'px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 cursor-pointer',
                                page === techniciens.current_page ? 'font-semibold text-red-600 border-red-400' : ''
                              ]">
                            {{ page }}
                        </span>

                        <button :disabled="!techniciens.next_page_url" @click="fetchTechniciens(techniciens.current_page + 1)"
                            class="px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 flex items-center gap-1 disabled:opacity-50">
                            Suivant <i class="fas fa-chevron-right text-xs"></i>
                        </button>
                    </div>
                </div>

            </section>
        </app-template>
    </div>
</template>

<script>
import TechForm from './Components/TechForm.vue'

export default {
    name: "TechParamList",
    components: {
        TechForm
    },
    data() {
        return {
            techniciens: {
                data: [],
                current_page: 1,
                total: 0,
                from: 0,
                to: 0
            },
            filters: {
                search: '',
            },
            searchTimeout: null,
            loading: false,
            pages: [],

            technicienForm: {},

            selectedLine: null,

            loadingSaveTech: false,
        }
    },
    async mounted() {
        await this.fetchTechniciens()
    },
    watch: {
        'filters.search': function () {
            this.debouncedFetch()
        },
    },
    methods: {
        async fetchTechniciens(page = 1) {
            this.loading = true
            try {
                const params = {
                    page,
                    search: this.filters.search || '',
                    with_adresse: 1
                }
                const res = await this.$request.get('/techniciens/list', { params })
                this.techniciens = res
                this.generatePagination()
            } catch (e) {
                this.$toast.error('Erreur lors du chargement des techniciens')
            } finally {
                // petit délai pour lisser la transition visuelle
                setTimeout(() => { this.loading = false }, 250)
            }
        },
        debouncedFetch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.fetchTechniciens()
            }, 400)
        },
        generatePagination() {
            if (!this.techniciens.last_page) return
            const totalPages = this.techniciens.last_page
            const current = this.techniciens.current_page
            const pages = []
            for (let i = Math.max(1, current - 2); i <= Math.min(totalPages, current + 2); i++) {
                pages.push(i)
            }
            this.pages = pages
        },

        async editAddTechnicien() {
            const validation = this.$inpError([
                'inp-nom:required|min=2',
                'inp-prenom:required|min=2',
                'inp-email:required|email',
                'inp-telephone:nullable|tel'
            ]);

            if (!validation) {
                this.$toast.error('Formulaire invalide');
                return;
            }

            try {
                this.loadingSaveTech = true;
                let url = this.technicienForm.id ? `/techniciens/update/${this.technicienForm.id}` : `/techniciens/create`;
                let response = await this.$request.post(url, this.technicienForm);
                this.loadingSaveTech = false

                if (!response.valid) {
                    this.$toast.error(response.message || "Une erreur est survenue");
                    return;
                }

                // if other technicians are affected, we refresh the list
                if ((this.technicienForm.id && this.technicienForm.id !== this.$account.account.id) || !this.technicienForm.id) {
                    await this.fetchTechniciens();
                    if (this.technicienForm.id) {
                        this.$toast.success('Technicien modifié');
                    } else {
                        this.$toast.success('Technicien ajouté');
                    }
    
                    // Reset propre
                    this.$modal.hide('modal-edit-add-tech');
                    this.technicienForm = {};
                    this.adresseForm = {};
                } else {
                    location.reload();
                }

            } catch (e) {
                console.error(e);
                this.$toast.error("Erreur lors de l'ajout du technicien");
            }
        },
    }
}
</script>

<style scoped>
button[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
}
</style>
